import { useState, useEffect } from 'react';
import { draftService, PostDraft } from '../../services/draftService';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../UI/ConfirmModal';

export default function DraftsPage() {
  const [drafts, setDrafts] = useState<PostDraft[]>([]);
  const [autoSave, setAutoSave] = useState<PostDraft | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDrafts();
  }, []);

  const loadDrafts = async () => {
    const [allDrafts, autoSaveDraft] = await Promise.all([
      draftService.getDrafts(),
      draftService.getAutoSave()
    ]);
    // Sort drafts in reverse chronological order (newest first)
    const sortedDrafts = allDrafts.sort((a, b) => {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
    setDrafts(sortedDrafts);
    setAutoSave(autoSaveDraft);
  };

  const handleDeleteDraft = (id: string) => {
    setDraftToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!draftToDelete) return;

    try {
      if (draftToDelete === 'auto-save') {
        await draftService.clearAutoSave();
        setAutoSave(null);
      } else {
        await draftService.deleteDraft(draftToDelete);
        setDrafts(drafts.filter(d => d.id !== draftToDelete));
      }
    } catch (error) {
      console.error('Failed to delete draft:', error);
    }

    setShowDeleteConfirm(false);
    setDraftToDelete(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const allDrafts = [
    ...(autoSave ? [autoSave] : []),
    ...drafts
  ];

  return (
    <div className="space-y-4">
      <div className="theme-bg-primary rounded-lg shadow-sm border theme-border-primary p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold theme-text-primary">Post Drafts</h2>
          <div className="text-sm theme-text-tertiary">
            {allDrafts.length} {allDrafts.length === 1 ? 'draft' : 'drafts'}
          </div>
        </div>

        {allDrafts.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 theme-bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 theme-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium theme-text-primary mb-2">No drafts yet</h3>
            <p className="theme-text-secondary mb-4">
              Start writing a post and save it as a draft to see it here
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {allDrafts.map((draft) => (
              <div
                key={draft.id}
                className="border theme-border-primary rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      {draft.id === 'auto-save' && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 px-2 py-1 rounded-full font-medium">
                          Auto-saved
                        </span>
                      )}
                      <span className="text-xs bg-healthy-green/10 text-healthy-green px-2 py-1 rounded-full font-medium">
                        {draft.selectedCategory}
                      </span>
                      <span className="text-xs theme-text-tertiary">
                        {formatDate(draft.updatedAt)}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-medium theme-text-primary mb-2 line-clamp-1">
                      {draft.title || 'Untitled draft'}
                    </h3>
                    
                    <p className="theme-text-secondary text-sm mb-3 line-clamp-2">
                      {draft.description || 'No description'}
                    </p>

                    <div className="flex items-center space-x-4 text-xs theme-text-tertiary">
                      <span>{draft.title.length}/65 characters</span>
                      <span>{draft.description.length}/1100 characters</span>
                      {draft.showPoll && (
                        <span className="flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          Poll ({draft.pollOptions.length} options)
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => {
                        // Store the selected draft for the CreatePost component to pick up
                        localStorage.setItem('selectedDraft', JSON.stringify(draft));
                        navigate('/');
                      }}
                      className="text-healthy-green hover:bg-green-50 dark:hover:bg-green-900/20 p-2 rounded-lg transition-colors"
                      title="Continue editing"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    
                    <button
                      onClick={() => handleDeleteDraft(draft.id)}
                      className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-lg transition-colors"
                      title="Delete draft"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDraftToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Draft"
        message={
          draftToDelete === 'auto-save' 
            ? "Are you sure you want to delete this auto-saved draft? This action cannot be undone."
            : "Are you sure you want to delete this draft? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
      />
    </div>
  );
}